import { lazy } from 'react';
import { IconAlertTriangle } from '@tabler/icons-react';

import { ObstructionDetailContent } from '@/components/obstructions/ObstructionDetailsPanel/ObstructionDetailContent';
import { selectHasActiveObstructionFilters } from '@/store/selectors/obstructionFilterSelectors';
import { selectArtifactObstructionsVisible } from '@/store/selectors/layerSelectors';
import type { GeoMarkupStore } from '@/store';
import { VIEW_MODE, type ViewMode } from '@/types';

import { ObstructionFormPanel } from './ObstructionFormPanel';
import type { ArtifactDefinition } from './registry';

/**
 * `BackgroundPage` is lazy-loaded to break the registry → obstructions →
 * page → hooks → registry import cycle that would otherwise form and to
 * keep the initial bundle small. Mirrors `tasksArtifact` / `notesArtifact`.
 */
const ObstructionsPage = lazy(() =>
  import('@/pages/ObstructionsPage/ObstructionsPage').then((module) => ({
    default: module.ObstructionsPage,
  }))
);

/** Lazy-loaded for the same cycle-breaking reason as `NotesPanelContent`. */
const ObstructionsPanelContent = lazy(() =>
  import('@/components/ui/SidePanel/ObstructionsPanelContent').then(
    (module) => ({ default: module.ObstructionsPanelContent })
  )
);

export const obstructionsArtifact: ArtifactDefinition = {
  id: 'obstructions',
  singular: 'obstruction',
  icon: IconAlertTriangle,
  labelKey: 'appShell.sidebar.obstructions',
  supportedViews: [VIEW_MODE.MAP, VIEW_MODE.LIST, VIEW_MODE.KANBAN] as const,
  defaultView: VIEW_MODE.MAP,
  BackgroundPage: ObstructionsPage,
  PanelContent: ObstructionsPanelContent,
  DetailContent: ObstructionDetailContent,
  FormPanel: ObstructionFormPanel,
  getSlice: (state) => state.obstructions,
  selectors: {
    hasActiveFilters: selectHasActiveObstructionFilters,
    isLayerVisible: selectArtifactObstructionsVisible,
  },
  syncViewMode: (view: ViewMode, store: GeoMarkupStore): void => {
    if (store.ui.obstructionViewMode !== view) {
      store.ui.setObstructionViewMode(view);
    }
  },
};
